<!-- GroupEntryModal.vue -->
<template>
  <div class="modal fade" id="groupEntryModal" tabindex="-1" role="dialog">
    <div class="modal-dialog modal-xl modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            <i class="mdi mdi-bus text-primary me-2"></i>
            Group Entry Registration
          </h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
        </div>

        <form @submit.prevent="createGroupEntry">
          <div class="modal-body">
            <!-- Group Type Selection -->
            <div class="row mb-4">
              <div class="col-12">
                <label class="form-label fw-semibold">Select Group Type</label>
                <div class="row g-3">
                  <div class="col-md-3" v-for="type in groupTypes" :key="type.value">
                    <div class="card border cursor-pointer group-type-card"
                         :class="{'border-primary bg-light': groupForm.groupType === type.value}"
                         @click="selectGroupType(type.value)">
                      <div class="card-body text-center p-3">
                        <i :class="type.icon + ' font-size-24 mb-2'"
                           :style="{color: groupForm.groupType === type.value ? '#0d6efd' : '#6c757d'}"></i>
                        <h6 class="mb-1">{{ type.label }}</h6>
                        <small class="text-muted">{{ type.description }}</small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Organization Information -->
            <div class="row mb-3">
              <div class="col-12">
                <h6 class="fw-semibold mb-3">Organization/Group Information</h6>
              </div>
            </div>

            <div class="row g-3 mb-3">
              <div class="col-md-6">
                <label class="form-label">Organization/Group Name *</label>
                <input type="text" class="form-control" v-model="groupForm.organizationName"
                       placeholder="Enter organization or group name" required>
              </div>
              <div class="col-md-6">
                <label class="form-label">Registration/License Number</label>
                <input type="text" class="form-control" v-model="groupForm.registrationNumber"
                       placeholder="Enter registration number">
              </div>
            </div>

            <div class="row g-3 mb-3">
              <div class="col-md-6">
                <label class="form-label">Group Leader Name *</label>
                <input type="text" class="form-control" v-model="groupForm.leaderName"
                       placeholder="Enter group leader name" required>
              </div>
              <div class="col-md-6">
                <label class="form-label">Leader ID/Passport *</label>
                <input type="text" class="form-control" v-model="groupForm.leaderId"
                       placeholder="Enter ID or passport number" required>
              </div>
            </div>

            <div class="row g-3 mb-3">
              <div class="col-md-6">
                <label class="form-label">Contact Phone *</label>
                <div class="input-group">
                  <span class="input-group-text">+254</span>
                  <input type="tel" class="form-control" v-model="groupForm.phoneNumber"
                         placeholder="712345678" required pattern="[0-9]{9}">
                </div>
              </div>
              <div class="col-md-6">
                <label class="form-label">Email Address *</label>
                <input type="email" class="form-control" v-model="groupForm.email"
                       placeholder="group@example.com" required>
              </div>
            </div>

            <!-- Group Composition -->
            <div class="row mb-3">
              <div class="col-12">
                <h6 class="fw-semibold mb-3">Group Composition</h6>
              </div>
            </div>

            <div class="row g-3 mb-3">
              <div class="col-md-3">
                <label class="form-label">Adults (16+ years) *</label>
                <input type="number" class="form-control" v-model.number="groupForm.adults"
                       @change="calculateGroupFees()" min="0" required>
              </div>
              <div class="col-md-3">
                <label class="form-label">Children (3-15 years)</label>
                <input type="number" class="form-control" v-model.number="groupForm.children"
                       @change="calculateGroupFees()" min="0">
              </div>
              <div class="col-md-3">
                <label class="form-label">Tour Guides/Staff</label>
                <input type="number" class="form-control" v-model.number="groupForm.guides"
                       @change="calculateGroupFees()" min="0">
                <small class="text-muted">Free entry for guides</small>
              </div>
              <div class="col-md-3">
                <label class="form-label">Total Group Size</label>
                <input type="text" class="form-control" :value="totalGroupSize" readonly>
              </div>
            </div>

            <!-- Nationality Mix for Tour Groups -->
            <div v-if="groupForm.groupType === 'TOUR_GROUP'" class="row mb-3">
              <div class="col-12">
                <h6 class="fw-semibold mb-3">Visitor Nationality Mix</h6>
              </div>
              <div class="col-12">
                <div class="table-responsive">
                  <table class="table table-sm">
                    <thead>
                    <tr>
                      <th>Category</th>
                      <th>Adults</th>
                      <th>Children</th>
                      <th>Rate (Adult/Child)</th>
                      <th>Subtotal</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr>
                      <td>Citizens (Kenya)</td>
                      <td>
                        <input type="number" class="form-control form-control-sm"
                               v-model.number="groupForm.nationalityMix.citizen.adults"
                               @change="calculateGroupFees()" min="0">
                      </td>
                      <td>
                        <input type="number" class="form-control form-control-sm"
                               v-model.number="groupForm.nationalityMix.citizen.children"
                               @change="calculateGroupFees()" min="0">
                      </td>
                      <td>{{ formatPrice(200) }} / {{ formatPrice(100) }}</td>
                      <td class="text-end">{{ formatPrice(getNationalitySubtotal('citizen')) }}</td>
                    </tr>
                    <tr>
                      <td>Residents (East Africa)</td>
                      <td>
                        <input type="number" class="form-control form-control-sm"
                               v-model.number="groupForm.nationalityMix.resident.adults"
                               @change="calculateGroupFees()" min="0">
                      </td>
                      <td>
                        <input type="number" class="form-control form-control-sm"
                               v-model.number="groupForm.nationalityMix.resident.children"
                               @change="calculateGroupFees()" min="0">
                      </td>
                      <td>{{ formatPrice(500) }} / {{ formatPrice(250) }}</td>
                      <td class="text-end">{{ formatPrice(getNationalitySubtotal('resident')) }}</td>
                    </tr>
                    <tr>
                      <td>Non-Residents (International)</td>
                      <td>
                        <input type="number" class="form-control form-control-sm"
                               v-model.number="groupForm.nationalityMix.nonResident.adults"
                               @change="calculateGroupFees()" min="0">
                      </td>
                      <td>
                        <input type="number" class="form-control form-control-sm"
                               v-model.number="groupForm.nationalityMix.nonResident.children"
                               @change="calculateGroupFees()" min="0">
                      </td>
                      <td>{{ formatPrice(2600) }} / {{ formatPrice(1300) }}</td>
                      <td class="text-end">{{ formatPrice(getNationalitySubtotal('nonResident')) }}</td>
                    </tr>
                    </tbody>
                  </table>
                </div>
                <div v-if="nationalityMixError" class="alert alert-danger mt-2">
                  <i class="mdi mdi-alert me-2"></i>{{ nationalityMixError }}
                </div>
              </div>
            </div>

            <!-- Vehicle Information -->
            <div class="row mb-3">
              <div class="col-12">
                <h6 class="fw-semibold mb-3">Vehicle Information</h6>
              </div>
            </div>

            <div class="row">
              <div class="col-12">
                <div class="table-responsive">
                  <table class="table">
                    <thead>
                    <tr>
                      <th>Vehicle Registration</th>
                      <th>Vehicle Type</th>
                      <th>Capacity</th>
                      <th>Driver Name</th>
                      <th>Fee</th>
                      <th>Action</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="(vehicle, index) in groupForm.vehicles" :key="index">
                      <td>
                        <input type="text" class="form-control form-control-sm"
                               v-model="vehicle.registration" placeholder="KAA 123B" required>
                      </td>
                      <td>
                        <select class="form-select form-select-sm" v-model="vehicle.type"
                                @change="calculateGroupFees()" required>
                          <option value="">Select Type</option>
                          <option value="Safari Van">Safari Van</option>
                          <option value="Tour Bus">Tour Bus</option>
                          <option value="School Bus">School Bus</option>
                          <option value="Minibus">Minibus</option>
                        </select>
                      </td>
                      <td>
                        <input type="number" class="form-control form-control-sm"
                               v-model.number="vehicle.capacity" min="1" placeholder="25">
                      </td>
                      <td>
                        <input type="text" class="form-control form-control-sm"
                               v-model="vehicle.driverName" placeholder="Driver name">
                      </td>
                      <td class="text-end">{{ formatPrice(getVehicleFee(vehicle.type)) }}</td>
                      <td>
                        <button type="button" @click="removeVehicle(index)"
                                class="btn btn-sm btn-danger" :disabled="groupForm.vehicles.length === 1">
                          <i class="mdi mdi-delete"></i>
                        </button>
                      </td>
                    </tr>
                    </tbody>
                    <tfoot>
                    <tr>
                      <td colspan="6">
                        <button type="button" @click="addVehicle()" class="btn btn-sm btn-success">
                          <i class="mdi mdi-plus me-1"></i>Add Vehicle
                        </button>
                      </td>
                    </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>

            <!-- Additional Services -->
            <div class="row mb-3">
              <div class="col-12">
                <h6 class="fw-semibold mb-3">Additional Services</h6>
              </div>
            </div>

            <div class="row g-3 mb-3">
              <div class="col-md-6">
                <div class="form-check">
                  <input class="form-check-input" type="checkbox" v-model="groupForm.services.tourGuide"
                         @change="calculateGroupFees()" id="parkGuide">
                  <label class="form-check-label" for="parkGuide">
                    <i class="mdi mdi-account-tie text-primary me-2"></i>
                    Park Tour Guide (+{{ formatPrice(5000) }}/group)
                  </label>
                </div>
                <div class="form-check">
                  <input class="form-check-input" type="checkbox" v-model="groupForm.services.lunchArea"
                         @change="calculateGroupFees()" id="lunchArea">
                  <label class="form-check-label" for="lunchArea">
                    <i class="mdi mdi-food text-success me-2"></i>
                    Lunch Area Reservation (+{{ formatPrice(2000) }})
                  </label>
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-check">
                  <input class="form-check-input" type="checkbox" v-model="groupForm.services.specialRoute"
                         @change="calculateGroupFees()" id="specialRoute">
                  <label class="form-check-label" for="specialRoute">
                    <i class="mdi mdi-map-marker-path text-warning me-2"></i>
                    Special Route Access (+{{ formatPrice(10000) }})
                  </label>
                </div>
                <div class="form-check">
                  <input class="form-check-input" type="checkbox" v-model="groupForm.services.photography"
                         @change="calculateGroupFees()" id="groupPhotography">
                  <label class="form-check-label" for="groupPhotography">
                    <i class="mdi mdi-camera text-info me-2"></i>
                    Group Photography Permit (+{{ formatPrice(8000) }})
                  </label>
                </div>
              </div>
            </div>

            <!-- Visit Details -->
            <div class="row g-3 mb-3">
              <div class="col-md-4">
                <label class="form-label">Entry Date *</label>
                <input type="date" class="form-control" v-model="groupForm.entryDate"
                       :min="today" required>
              </div>
              <div class="col-md-4">
                <label class="form-label">Entry Time *</label>
                <input type="time" class="form-control" v-model="groupForm.entryTime" required>
              </div>
              <div class="col-md-4">
                <label class="form-label">Entry Gate *</label>
                <select class="form-select" v-model="groupForm.entryGate" required>
                  <option value="">Select Gate</option>
                  <option value="Kimana Gate">Kimana Gate</option>
                  <option value="Meshanani Gate">Meshanani Gate</option>
                </select>
              </div>
            </div>

            <!-- Payment Information -->
            <div class="row mb-3">
              <div class="col-12">
                <h6 class="fw-semibold mb-3">Payment Information</h6>
              </div>
            </div>

            <div class="row g-3 mb-3">
              <div class="col-md-6">
                <label class="form-label">Payment Method *</label>
                <select class="form-select" v-model="groupForm.paymentMethod" required>
                  <option value="">Select Payment Method</option>
                  <option value="Bank Transfer">Bank Transfer</option>
                  <option value="Corporate Cheque">Corporate Cheque</option>
                  <option value="Credit Card">Credit Card</option>
                  <option value="Invoice">Invoice (Net 30)</option>
                  <option value="Cash">Cash</option>
                </select>
              </div>
              <div class="col-md-6" v-if="groupForm.paymentMethod === 'Bank Transfer'">
                <label class="form-label">Transaction Reference</label>
                <input type="text" class="form-control" v-model="groupForm.transactionRef"
                       placeholder="Bank reference number">
              </div>
            </div>

            <!-- Group Discount -->
            <div class="row g-3 mb-3">
              <div class="col-md-6">
                <label class="form-label">Group Discount</label>
                <select class="form-select" v-model="groupForm.discountType" @change="calculateGroupFees()">
                  <option value="">No Discount</option>
                  <option value="VOLUME_10" v-if="totalGroupSize >= 20">10% (20+ visitors)</option>
                  <option value="VOLUME_15" v-if="totalGroupSize >= 50">15% (50+ visitors)</option>
                  <option value="SCHOOL">School Group (30%)</option>
                  <option value="REPEAT">Repeat Customer (5%)</option>
                </select>
              </div>
              <div class="col-md-6" v-if="groupForm.discountType">
                <label class="form-label">Discount Amount</label>
                <input type="text" class="form-control" :value="formatPrice(groupFeeBreakdown.discountAmount)" readonly>
              </div>
            </div>

            <!-- Fee Summary -->
            <div class="row mb-3">
              <div class="col-12">
                <div class="card bg-light">
                  <div class="card-header">
                    <h6 class="mb-0">Group Fee Summary</h6>
                  </div>
                  <div class="card-body">
                    <div class="row">
                      <div class="col-md-8">
                        <table class="table table-sm mb-0">
                          <tbody>
                          <tr>
                            <td>Park Entry Fees</td>
                            <td class="text-end">{{ formatPrice(groupFeeBreakdown.entryFees) }}</td>
                          </tr>
                          <tr>
                            <td>Vehicle Fees ({{ groupForm.vehicles.length }} vehicles)</td>
                            <td class="text-end">{{ formatPrice(groupFeeBreakdown.vehicleFees) }}</td>
                          </tr>
                          <tr v-if="groupFeeBreakdown.serviceFees > 0">
                            <td>Additional Services</td>
                            <td class="text-end">{{ formatPrice(groupFeeBreakdown.serviceFees) }}</td>
                          </tr>
                          <tr v-if="groupFeeBreakdown.discountAmount > 0">
                            <td>Group Discount ({{ getDiscountPercentage() }})</td>
                            <td class="text-end text-danger">-{{ formatPrice(groupFeeBreakdown.discountAmount) }}</td>
                          </tr>
                          <tr class="table-active">
                            <td><strong>Total Amount Due</strong></td>
                            <td class="text-end">
                              <h5 class="text-success mb-0">{{ formatPrice(groupFeeBreakdown.totalFees) }}</h5>
                            </td>
                          </tr>
                          </tbody>
                        </table>
                      </div>
                      <div class="col-md-4">
                        <div class="alert alert-info mb-0">
                          <h6>Quick Stats</h6>
                          <small>
                            Total Visitors: {{ totalGroupSize }}<br>
                            Cost per Person: {{ formatPrice(groupFeeBreakdown.totalFees / totalGroupSize) }}<br>
                            Vehicles: {{ groupForm.vehicles.length }}
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Special Instructions -->
            <div class="row">
              <div class="col-12">
                <label class="form-label">Special Instructions / Notes</label>
                <textarea class="form-control" v-model="groupForm.notes"
                          placeholder="Any special requirements, dietary restrictions, or instructions..." rows="3"></textarea>
              </div>
            </div>
          </div>

          <div class="modal-footer">
            <div class="d-flex justify-content-between w-100">
              <div class="text-muted">
                <small>* Required fields</small>
              </div>
              <div>
                <button type="button" class="btn btn-secondary me-2" data-bs-dismiss="modal">Cancel</button>
                <button type="button" @click="saveAsDraft()" class="btn btn-warning me-2">
                  <i class="mdi mdi-content-save me-1"></i>Save Draft
                </button>
                <button type="submit" class="btn btn-primary" :disabled="loading || groupFeeBreakdown.totalFees === 0">
                  <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status"></span>
                  <i v-else class="mdi mdi-check me-2"></i>
                  {{ loading ? 'Processing...' : 'Register Group ('+formatPrice(groupFeeBreakdown.totalFees)+')' }}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import moment from 'moment';

export default {
  name: "GroupEntryModal",
  props: {
    feeStructure: {
      type: Object,
      default: () => ({
        entry: [
          { category: 'Citizen', adult: 200, child: 100 },
          { category: 'Resident', adult: 500, child: 250 },
          { category: 'Non-Resident', adult: 2600, child: 1300 },
          { category: 'Student', adult: 200, child: 100 }
        ],
        vehicle: [
          { type: 'Safari Van', amount: 1500 },
          { type: 'Tour Bus', amount: 2000 },
          { type: 'School Bus', amount: 1000 },
          { type: 'Minibus', amount: 1200 }
        ]
      })
    }
  },
  data() {
    return {
      loading: false,
      today: moment().format('YYYY-MM-DD'),
      nationalityMixError: '',

      groupForm: {
        groupType: 'TOUR_GROUP',
        organizationName: '',
        registrationNumber: '',
        leaderName: '',
        leaderId: '',
        phoneNumber: '',
        email: '',
        adults: 10,
        children: 5,
        guides: 2,
        nationalityMix: {
          citizen: { adults: 0, children: 0 },
          resident: { adults: 0, children: 0 },
          nonResident: { adults: 0, children: 0 }
        },
        vehicles: [
          {
            registration: '',
            type: '',
            capacity: '',
            driverName: ''
          }
        ],
        services: {
          tourGuide: false,
          lunchArea: false,
          specialRoute: false,
          photography: false
        },
        entryDate: '',
        entryTime: '',
        entryGate: '',
        paymentMethod: '',
        transactionRef: '',
        discountType: '',
        notes: ''
      },

      groupTypes: [
        {
          value: 'TOUR_GROUP',
          label: 'Tour Group',
          description: 'Safari & tours',
          icon: 'mdi mdi-bus'
        },
        {
          value: 'SCHOOL_GROUP',
          label: 'School Group',
          description: 'Educational visit',
          icon: 'mdi mdi-school'
        },
        {
          value: 'CORPORATE_GROUP',
          label: 'Corporate',
          description: 'Company outing',
          icon: 'mdi mdi-office-building'
        },
        {
          value: 'SPECIAL_EVENT',
          label: 'Special Event',
          description: 'Events & ceremonies',
          icon: 'mdi mdi-calendar-star'
        }
      ],

      groupFeeBreakdown: {
        entryFees: 0,
        vehicleFees: 0,
        serviceFees: 0,
        discountAmount: 0,
        totalFees: 0
      }
    }
  },
  computed: {
    totalGroupSize() {
      return this.groupForm.adults + this.groupForm.children + this.groupForm.guides;
    }
  },
  methods: {
    formatPrice(value) {
      return new Intl.NumberFormat('en-KE', {
        style: 'currency',
        currency: 'KES',
        minimumFractionDigits: 0
      }).format(value || 0);
    },

    selectGroupType(type) {
      this.groupForm.groupType = type;

      // Set defaults based on group type
      if (type === 'SCHOOL_GROUP') {
        this.groupForm.discountType = 'SCHOOL';
      } else {
        this.groupForm.discountType = '';
      }

      this.calculateGroupFees();
    },

    getNationalitySubtotal(category) {
      const rates = {
        citizen: { adult: 200, child: 100 },
        resident: { adult: 500, child: 250 },
        nonResident: { adult: 2600, child: 1300 }
      };

      const mix = this.groupForm.nationalityMix[category];
      return (mix.adults * rates[category].adult) + (mix.children * rates[category].child);
    },

    getVehicleFee(type) {
      if (!type) return 0;
      const vehicle = this.feeStructure.vehicle.find(v => v.type === type);
      return vehicle ? vehicle.amount : 0;
    },

    addVehicle() {
      this.groupForm.vehicles.push({
        registration: '',
        type: '',
        capacity: '',
        driverName: ''
      });
    },

    removeVehicle(index) {
      if (this.groupForm.vehicles.length > 1) {
        this.groupForm.vehicles.splice(index, 1);
        this.calculateGroupFees();
      }
    },

    getDiscountPercentage() {
      const discounts = {
        'VOLUME_10': '10%',
        'VOLUME_15': '15%',
        'SCHOOL': '30%',
        'REPEAT': '5%'
      };
      return discounts[this.groupForm.discountType] || '0%';
    },

    validateNationalityMix() {
      if (this.groupForm.groupType !== 'TOUR_GROUP') return true;

      const totalAdults = this.groupForm.nationalityMix.citizen.adults +
          this.groupForm.nationalityMix.resident.adults +
          this.groupForm.nationalityMix.nonResident.adults;

      const totalChildren = this.groupForm.nationalityMix.citizen.children +
          this.groupForm.nationalityMix.resident.children +
          this.groupForm.nationalityMix.nonResident.children;

      if (totalAdults !== this.groupForm.adults || totalChildren !== this.groupForm.children) {
        this.nationalityMixError = `Nationality breakdown must match total: ${this.groupForm.adults} adults, ${this.groupForm.children} children`;
        return false;
      }

      this.nationalityMixError = '';
      return true;
    },

    calculateGroupFees() {
      // Validate nationality mix for tour groups
      if (this.groupForm.groupType === 'TOUR_GROUP') {
        this.validateNationalityMix();
      }

      // Calculate entry fees
      let entryFees = 0;

      if (this.groupForm.groupType === 'TOUR_GROUP' && this.validateNationalityMix()) {
        // Use nationality mix for tour groups
        entryFees = this.getNationalitySubtotal('citizen') +
            this.getNationalitySubtotal('resident') +
            this.getNationalitySubtotal('nonResident');
      } else {
        // Use default rates for other group types
        const defaultRate = this.groupForm.groupType === 'SCHOOL_GROUP' ? 'Student' : 'Citizen';
        const fee = this.feeStructure.entry.find(f => f.category === defaultRate);
        if (fee) {
          entryFees = (this.groupForm.adults * fee.adult) + (this.groupForm.children * fee.child);
        }
      }

      this.groupFeeBreakdown.entryFees = entryFees;

      // Calculate vehicle fees
      let vehicleFees = 0;
      this.groupForm.vehicles.forEach(vehicle => {
        vehicleFees += this.getVehicleFee(vehicle.type);
      });
      this.groupFeeBreakdown.vehicleFees = vehicleFees;

      // Calculate service fees
      let serviceFees = 0;
      if (this.groupForm.services.tourGuide) serviceFees += 5000;
      if (this.groupForm.services.lunchArea) serviceFees += 2000;
      if (this.groupForm.services.specialRoute) serviceFees += 10000;
      if (this.groupForm.services.photography) serviceFees += 8000;
      this.groupFeeBreakdown.serviceFees = serviceFees;

      // Calculate discount
      const subtotal = entryFees + vehicleFees + serviceFees;
      let discountAmount = 0;

      switch (this.groupForm.discountType) {
        case 'VOLUME_10':
          discountAmount = subtotal * 0.10;
          break;
        case 'VOLUME_15':
          discountAmount = subtotal * 0.15;
          break;
        case 'SCHOOL':
          discountAmount = subtotal * 0.30;
          break;
        case 'REPEAT':
          discountAmount = subtotal * 0.05;
          break;
      }

      this.groupFeeBreakdown.discountAmount = Math.round(discountAmount);
      this.groupFeeBreakdown.totalFees = subtotal - this.groupFeeBreakdown.discountAmount;
    },

    generateGroupTicketNumber() {
      const date = new Date();
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const sequence = String(Math.floor(Math.random() * 10000)).padStart(5, '0');
      return `AMB-GRP-${year}${month}${day}${sequence}`;
    },

    async createGroupEntry() {
      try {
        // Validate nationality mix
        if (this.groupForm.groupType === 'TOUR_GROUP' && !this.validateNationalityMix()) {
          alert('Please ensure nationality breakdown matches the total visitor count');
          return;
        }

        // Validate vehicles
        const validVehicles = this.groupForm.vehicles.filter(v => v.registration && v.type);
        if (validVehicles.length === 0) {
          alert('Please add at least one vehicle with registration and type');
          return;
        }

        this.loading = true;

        // Create group entry data
        const groupData = {
          ticketNumber: this.generateGroupTicketNumber(),
          groupType: this.groupForm.groupType,
          organizationName: this.groupForm.organizationName,
          registrationNumber: this.groupForm.registrationNumber,
          leaderName: this.groupForm.leaderName,
          leaderId: this.groupForm.leaderId,
          phoneNumber: '+254' + this.groupForm.phoneNumber,
          email: this.groupForm.email,
          adults: this.groupForm.adults,
          children: this.groupForm.children,
          guides: this.groupForm.guides,
          totalGroupSize: this.totalGroupSize,
          nationalityMix: this.groupForm.groupType === 'TOUR_GROUP' ? { ...this.groupForm.nationalityMix } : null,
          vehicles: validVehicles,
          services: { ...this.groupForm.services },
          entryDate: this.groupForm.entryDate,
          entryTime: this.groupForm.entryTime,
          entryGate: this.groupForm.entryGate,
          paymentMethod: this.groupForm.paymentMethod,
          transactionRef: this.groupForm.transactionRef,
          discountType: this.groupForm.discountType,
          discountAmount: this.groupFeeBreakdown.discountAmount,
          entryFees: this.groupFeeBreakdown.entryFees,
          vehicleFees: this.groupFeeBreakdown.vehicleFees,
          serviceFees: this.groupFeeBreakdown.serviceFees,
          totalFees: this.groupFeeBreakdown.totalFees,
          notes: this.groupForm.notes,
          status: 'PENDING',
          createdAt: new Date().toISOString()
        };

        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 2000));

        this.$emit('group-entry-created', groupData);

        // Show success message
        if (confirm('Group entry created successfully! Generate tickets now?')) {
          this.generateGroupTickets(groupData);
        }

        // Reset form
        this.resetForm();

        // Close modal
        const modal = bootstrap.Modal.getInstance(document.getElementById('groupEntryModal'));
        modal.hide();

      } catch (error) {
        console.error('Error creating group entry:', error);
        alert('Failed to create group entry. Please try again.');
      } finally {
        this.loading = false;
      }
    },

    generateGroupTickets(groupData) {
      // Generate individual tickets for the group
      console.log('Generating tickets for group:', groupData.ticketNumber);
      // Implementation would generate PDF tickets
    },

    saveAsDraft() {
      // Save current form data as draft
      const draftData = { ...this.groupForm };
      localStorage.setItem('groupEntryDraft', JSON.stringify(draftData));
      alert('Draft saved successfully!');
    },

    loadDraft() {
      const draft = localStorage.getItem('groupEntryDraft');
      if (draft) {
        this.groupForm = JSON.parse(draft);
        this.calculateGroupFees();
      }
    },

    resetForm() {
      this.groupForm = {
        groupType: 'TOUR_GROUP',
        organizationName: '',
        registrationNumber: '',
        leaderName: '',
        leaderId: '',
        phoneNumber: '',
        email: '',
        adults: 10,
        children: 5,
        guides: 2,
        nationalityMix: {
          citizen: { adults: 0, children: 0 },
          resident: { adults: 0, children: 0 },
          nonResident: { adults: 0, children: 0 }
        },
        vehicles: [
          {
            registration: '',
            type: '',
            capacity: '',
            driverName: ''
          }
        ],
        services: {
          tourGuide: false,
          lunchArea: false,
          specialRoute: false,
          photography: false
        },
        entryDate: '',
        entryTime: '',
        entryGate: '',
        paymentMethod: '',
        transactionRef: '',
        discountType: '',
        notes: ''
      };
      this.calculateGroupFees();
      localStorage.removeItem('groupEntryDraft');
    }
  },
  mounted() {
    this.calculateGroupFees();
    // Check for saved draft
    this.loadDraft();
  }
}
</script>

<style scoped>
.modal-dialog {
  max-width: 1200px;
}

.group-type-card {
  cursor: pointer;
  transition: all 0.3s ease;
}

.group-type-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.cursor-pointer {
  cursor: pointer;
}

.form-check-label {
  cursor: pointer;
}

.card {
  border-radius: 8px;
}

.bg-light {
  background-color: #f8f9fa !important;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}

.table-active {
  background-color: #f0f0f0;
}

.font-size-24 {
  font-size: 24px;
}

.form-control-sm {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
}

.table td {
  vertical-align: middle;
}

.alert {
  padding: 0.75rem 1rem;
}
</style>